/**
 * Combat aggression tunables and auto-attack target selection.
 *
 * Pulled out of `views/farm/resources.ts` so the numbers that decide "does the
 * hero pick this fight?" are testable without Pixi, a farm context, or a canvas.
 * resources.ts keeps the swinging; this file decides *whether* and *at what*.
 *
 * The whole file exists because auto-attack was picking fights the player never
 * asked for: it acquired anything within 7 tiles, set the click-to-engage lock as
 * a side effect, and then the lock pursued that target for another 16 tiles. Walk
 * past a sleeping orc and you were committed to a chase. The rules below are the
 * inverse: auto-attack DEFENDS (only hits things already coming for you, and only
 * at arm's length), and only a deliberate click starts and sustains a fight.
 */

import { TILE } from '../map/world';

/**
 * How far auto-attack will reach for a target.
 *
 * Was 7 tiles — most of the visible screen, so simply walking across the farm
 * with the knife out started swinging at everything. 2.5 tiles is barely past
 * ATTACK_REACH (1.6), so the hero finishes what is already on top of it and takes
 * at most a step to do so; it never crosses the field to open a fight.
 */
export const AUTO_RANGE = 2.5 * TILE;

/**
 * Auto-attack swing cadence.
 *
 * Was 650ms, which on top of combatTargetTick's identical timer read as a
 * continuous flurry the player had no say in. 1100ms is roughly one swing per
 * mob attack cadence — still a real contribution, but slow enough that the fight
 * is legible and disengaging between swings is possible.
 */
export const AUTO_CADENCE_MS = 1100;

/**
 * Click-to-engage leash: how far the hero will pursue a mob it was told to kill.
 *
 * Was 16 tiles (a third of the screen in each direction), which meant one
 * mis-click dragged the hero into the dark after a fleeing mob. 6 tiles is about
 * the distance you can see the fight happening in, and it is inside the mobs'
 * own de-aggro leash, so a mob that gives up and walks home ends the engagement
 * rather than towing the player along behind it.
 */
export const ENGAGE_LEASH = 6 * TILE;

/** The minimum shape of a target the selector needs. Keeps this file Pixi-free. */
export interface AutoTarget {
  x: number;
  y: number;
  /** Mob states; fauna pass their own ('dead' / anything else). */
  state: string;
  /** True when this thing is currently hunting the player (mobs only). */
  hostile?: boolean;
  /** Fauna only: false for cows/horses/etc. that must never be auto-swung at. */
  killable?: boolean;
}

export interface AutoPick {
  kind: 'mob' | 'animal';
  x: number;
  y: number;
}

/**
 * Choose what auto-attack should swing at this tick, or null for "nothing".
 *
 * Two rules carry the "less aggressive" requirement:
 *
 *  1. A mob is only a valid auto-target while it is ALREADY chasing/attacking the
 *     player. An idle mob standing in the dark, or one that de-aggroed and is
 *     walking home ('return'), is left alone — auto-attack no longer initiates,
 *     it only answers. Clicking one still works; that is the explicit choice.
 *  2. Animals rank below mobs and, like mobs, must be inside AUTO_RANGE — which
 *     is now short enough that you have to have walked up to the animal yourself.
 */
export function selectAutoTarget(
  mobs: readonly AutoTarget[],
  animals: readonly AutoTarget[],
  px: number,
  py: number,
  range = AUTO_RANGE,
): AutoPick | null {
  const d = (t: AutoTarget) => Math.hypot(t.x - px, t.y - py);
  let best: AutoPick | null = null;
  let bestD = range;

  for (const m of mobs) {
    if (m.state === 'dying' || m.state === 'spawning') continue;
    if (!m.hostile) continue;   // rule 1: defend only
    const dd = d(m);
    if (dd < bestD) { bestD = dd; best = { kind: 'mob', x: m.x, y: m.y }; }
  }
  if (best) return best;

  for (const a of animals) {
    if (a.state === 'dead') continue;
    if (a.killable === false) continue;
    const dd = d(a);
    if (dd < bestD) { bestD = dd; best = { kind: 'animal', x: a.x, y: a.y }; }
  }
  return best;
}

/** A mob state counts as "coming for you" only while it is actively hunting. */
export const isHostileState = (state: string) => state === 'chase' || state === 'attack';
